// Experiments on screen: starting one from Insights, the results card, the one-line
// reminder on Home, Morning prep and the Live session, and the evening check.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { formatMoney, currentTradingDay } from './pnl';
import { experimentProgress, experimentResults } from './experimentStats';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

export const activeExperiment = (list) => (list || []).find((x) => x.status === 'active') || null;

const LENGTHS = [10, 20];

// Window for starting an experiment from a suggestion in Insights.
export function StartExperiment({ item, onStarted, onClose, onSessionExpired }) {
  const { t } = useI18n();
  const [title, setTitle] = useState(item.tip);
  const [lengthDays, setLengthDays] = useState(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function start(e) {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const exp = await api('/experiments', {
        method: 'POST',
        body: { title: title.trim(), metric: item.metric, area: item.area, finding: item.finding, lengthDays, startedOn: currentTradingDay() },
      });
      onStarted(exp);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal experiment-modal" role="dialog" aria-modal="true" aria-labelledby="exp-title" onSubmit={start}>
        <div className="modal-header">
          <h2 id="exp-title">{t('Test a fix')}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <p className="hint">
          <strong>{item.area}:</strong> {item.finding}
        </p>
        <label className="routine-field">
          <span className="scale-label">{t('What you’ll do differently')}</span>
          <textarea rows="2" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <div className="experiment-length" role="group" aria-label={t('For how long?')}>
          <span className="scale-label">{t('For how long?')}</span>
          {LENGTHS.map((n) => (
            <button key={n} type="button" className={`secondary small${lengthDays === n ? ' active' : ''}`} aria-pressed={lengthDays === n} onClick={() => setLengthDays(n)}>
              {t('{n} trading days', { n })}
            </button>
          ))}
        </div>
        <p className="field-hint">
          {t('It shows on Home, Morning prep and the Live session, and your evening reflection asks whether you stuck to it. At the end, Insights compares the same numbers before and during.')}
        </p>
        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          <button type="submit" disabled={busy || !title.trim()}>{busy ? t('Starting…') : t('Start the experiment')}</button>
          <button type="button" className="secondary" onClick={onClose}>{t('Cancel')}</button>
        </div>
      </form>
    </div>
  );
}

// One line on Home, Morning prep and the Live session while an experiment runs.
export function ExperimentLine({ experiments }) {
  const { t } = useI18n();
  const exp = activeExperiment(experiments);
  if (!exp) return null;
  const p = experimentProgress(exp);
  return (
    <Link to="/app/insights" className="experiment-line">
      <span className="experiment-line-label">{p.finished ? t('Experiment finished') : t('Experiment · day {day} of {length}', { day: Math.max(1, p.day), length: p.length })}</span>
      <span className="experiment-line-title">{exp.title}</span>
      {p.finished && <span className="experiment-line-go">{t('See the results')} &rarr;</span>}
    </Link>
  );
}

// On the evening reflection: did you stick to it today?
export function ExperimentCheck({ experiments, day, update }) {
  const { t } = useI18n();
  const exp = activeExperiment(experiments);
  if (!exp) return null;
  const options = [['yes', 'Yes'], ['partly', 'Partly'], ['no', 'No']];
  return (
    <section className="focus-check experiment-check">
      <span className="focus-today-label">{t('Your experiment')}</span>
      <p className="focus-line">{exp.title}</p>
      <div className="focus-check-row" role="group" aria-label={t('Did you stick to it today?')}>
        <span>{t('Did you stick to it today?')}</span>
        {options.map(([id, label]) => (
          <button key={id} type="button" className={`secondary small${day.experimentFollowed === id ? ' active' : ''}`} aria-pressed={day.experimentFollowed === id}
            onClick={() => update({ experimentFollowed: day.experimentFollowed === id ? null : id })}>
            {t(label)}
          </button>
        ))}
      </div>
    </section>
  );
}

function fmtDate(key) {
  return new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
}

// Before vs during for one experiment.
function ResultsTable({ r, metric }) {
  const { t } = useI18n();
  const rate = (s) => (s.winRate === null ? '—' : `${Math.round(s.winRate)}%`);
  const tradingDays = (n) => t(n === 1 ? '{n} trading day' : '{n} trading days', { n });
  const winFirst = metric.kind === 'afterLoss' || metric.kind === 'afterWin';
  const rows = [
    [t('Trades'), (s) => `${s.count} (${s.tradesPerDay.toFixed(1)}/${t('day')})`],
    ...(winFirst ? [[t('Win rate'), rate]] : []),
    [t('Net P&L'), (s) => <span className={s.net > 0 ? 'gain' : s.net < 0 ? 'loss' : ''}>{formatMoney(s.net)}</span>],
    [t('Per trading day'), (s) => <span className={s.perDay > 0 ? 'gain' : s.perDay < 0 ? 'loss' : ''}>{formatMoney(s.perDay)}</span>],
    ...(winFirst ? [] : [[t('Win rate'), rate]]),
  ];
  return (
    <div className="table-wrap">
      <table className="experiment-table">
        <thead>
          <tr>
            <th scope="col"><span className="visually-hidden">{t('Measure')}</span></th>
            <th scope="col">{t('Before')} <em>{tradingDays(r.before.days)}</em></th>
            <th scope="col">{t('During')} <em>{tradingDays(r.during.days)}</em></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, cell]) => (
            <tr key={label}><th scope="row">{label}</th><td>{cell(r.before)}</td><td>{cell(r.during)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// The full experiment card in Insights: progress, results and what to do next.
export function ExperimentCard({ exp, trades, notes, onChanged, onRemoved, onSessionExpired }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const r = experimentResults(exp, trades, notes);
  const p = r.progress;
  const running = exp.status === 'active';

  async function patch(body) {
    setBusy(true);
    setError('');
    try {
      onChanged(await api(`/experiments/${exp._id}`, { method: 'PATCH', body }));
    } catch (err) {
      if (err.status === 401) onSessionExpired();
      else setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function keepAsRule() {
    setBusy(true);
    setError('');
    try {
      const routine = await api('/routine');
      const rules = routine.rules || [];
      if (!rules.includes(exp.title)) await api('/routine', { method: 'PUT', body: { rules: [...rules, exp.title].slice(0, 20) } });
      onChanged(await api(`/experiments/${exp._id}`, { method: 'PATCH', body: { status: 'kept', endedOn: exp.endedOn || currentTradingDay() } }));
    } catch (err) {
      if (err.status === 401) onSessionExpired();
      else setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api(`/experiments/${exp._id}`, { method: 'DELETE' });
      onRemoved(exp._id);
    } catch (err) {
      if (err.status === 401) onSessionExpired();
      else setError(err.message);
      setBusy(false);
    }
  }

  return (
    <section className={`experiment-card${running ? ' is-running' : ''}`}>
      <div className="experiment-head">
        <span className="experiment-badge">
          {exp.status === 'kept' ? t('Kept as a rule') : exp.status === 'ended' ? t('Ended') : p.finished ? t('Finished') : t('Day {day} of {length}', { day: Math.max(1, p.day), length: p.length })}
        </span>
        <span className="experiment-dates">{t('Started {date}', { date: fmtDate(exp.startedOn) })}</span>
      </div>
      <h3 className="experiment-title">{exp.title}</h3>
      {exp.finding && <p className="experiment-finding"><strong>{exp.area}:</strong> {exp.finding}</p>}
      {running && (
        <div className="status-bar experiment-progress" role="img" aria-label={t('Day {day} of {length}', { day: p.day, length: p.length })}>
          <span className="status-bar-fill target" style={{ width: `${(p.day / p.length) * 100}%` }} />
        </div>
      )}
      <ResultsTable r={r} metric={exp.metric} />
      <p className="experiment-note">
        {r.early && <span className="finding-small">{t('Early observation')}</span>}{' '}
        {r.adherence.checked > 0 && t('You stuck to it on {kept} of {checked} days you checked in.', r.adherence)}{' '}
        {t('This compares the same trades before and during. It shows what changed, not proof of why.')}
      </p>
      {error && <p className="error">{error}</p>}
      {running && (
        <div className="experiment-actions">
          {p.finished && <button type="button" className="small" disabled={busy} onClick={keepAsRule}>{t('Keep it as a rule')}</button>}
          {exp.lengthDays < 60 && (
            <button type="button" className="secondary small" disabled={busy} onClick={() => patch({ lengthDays: Math.min(60, exp.lengthDays + 10) })}>{t('Extend 10 days')}</button>
          )}
          <button type="button" className="secondary small" disabled={busy} onClick={() => patch({ status: 'ended', endedOn: currentTradingDay() })}>{t('End the experiment')}</button>
        </div>
      )}
      {!running && (
        <div className="experiment-actions">
          <button type="button" className="link-button" disabled={busy} onClick={remove}>{t('Delete')}</button>
        </div>
      )}
    </section>
  );
}

// The Insights section: the running experiment, then past ones.
export function ExperimentsSection({ experiments, setExperiments, trades, notes, onSessionExpired }) {
  const { t } = useI18n();
  const [showPast, setShowPast] = useState(false);
  const active = activeExperiment(experiments);
  const past = (experiments || []).filter((x) => x.status !== 'active');
  if (!active && !past.length) return null;
  const changed = (exp) => setExperiments((list) => list.map((x) => (x._id === exp._id ? exp : x)));
  const removed = (id) => setExperiments((list) => list.filter((x) => x._id !== id));
  return (
    <section className="experiments">
      <h2 className="experiments-heading">{t('Your experiment')}</h2>
      {active
        ? <ExperimentCard exp={active} trades={trades} notes={notes} onChanged={changed} onRemoved={removed} onSessionExpired={onSessionExpired} />
        : <p className="sidebar-muted">{t('No experiment running. Pick a suggestion under "What to try next" to start one.')}</p>}
      {past.length > 0 && (
        <>
          <button type="button" className="link-button experiments-past-toggle" onClick={() => setShowPast((s) => !s)}>
            {showPast ? t('Hide past experiments') : t('Past experiments ({n})', { n: past.length })}
          </button>
          {showPast && past.map((exp) => (
            <ExperimentCard key={exp._id} exp={exp} trades={trades} notes={notes} onChanged={changed} onRemoved={removed} onSessionExpired={onSessionExpired} />
          ))}
        </>
      )}
    </section>
  );
}
